import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion';
import styled, { css } from 'styled-components/macro';
import { WxCheckbox, WxLabel } from '../..';
import { AttributeKey } from '../../../hooks/api/queries/useAttributes';
import useProducts from '../../../hooks/api/queries/useProducts';
import {
  sortings,
  useFiltersContext,
} from '../../../hooks/context/useFiltersContext';
import { getPercentiles } from '../../../hooks/utils/getPercentiles';
import { __ } from '../../../i18n/l10n';
import { MobileOnly } from '../../layout/nav/Nav';
import { AttributeFilter } from './AttributeFilter';
import { FilterCards } from './FilterCards';
import { BackIcon } from './FiltersMenu';
import { RangeFilter } from './RangeFilter';
import { SortingFilter } from './SortingFilter';

interface Props {
  attrKeys: AttributeKey[];
  categoryId: number;
}

export const MobileFiltersAccordion = ({ attrKeys, categoryId }: Props) => {
  const {
    onlyAvailable,
    attributes,
    range,
    setOnlyAvailable,
    sorting,
    subCatList,
  } = useFiltersContext();

  const productsFilter = {
    categoryId,
    subCatList,
    attributes: [],
    attrKeyCount: 0,
    sort: [],
    skip: 0,
  };

  const { data } = useProducts(productsFilter);
  if (!data) return null;

  return (
    <MobileOnly>
      <FilterCards />
      <Accordion allowMultipleExpanded allowZeroExpanded>
        <StyledAccordionItem>
          <AccordionItemState>
            {({ expanded }) => (
              <>
                <Separator />

                <StyledAccordionItemHeading expanded={expanded}>
                  <StyledAccordionItemButton
                    $selected={Boolean(sorting) ?? onlyAvailable}
                  >
                    <WxLabel label={__('Sort')} black />

                    <BackIcon icon={expanded ? faChevronUp : faChevronDown} />
                  </StyledAccordionItemButton>
                </StyledAccordionItemHeading>

                <StyledAccordionItemPanel>
                  <UlCol>
                    {sortings.map((sorting) => (
                      <SortingFilter
                        sort={sorting}
                        key={`sort-mob-${sorting.value}`}
                      />
                    ))}
                    <DivOnlyAvailable>
                      <WxCheckbox
                        key={`attr-only-avail-mob`}
                        label={__('Show only products I can buy')}
                        noUppercase
                        selected={onlyAvailable}
                        onSelect={() => setOnlyAvailable(!onlyAvailable)}
                      />
                    </DivOnlyAvailable>
                  </UlCol>
                </StyledAccordionItemPanel>
              </>
            )}
          </AccordionItemState>
        </StyledAccordionItem>

        <StyledAccordionItem>
          <AccordionItemState>
            {({ expanded }) => (
              <>
                <Separator />

                <StyledAccordionItemHeading expanded={expanded}>
                  <StyledAccordionItemButton $selected={Boolean(range)}>
                    <WxLabel label={__('Miles')} black />

                    <BackIcon icon={expanded ? faChevronUp : faChevronDown} />
                  </StyledAccordionItemButton>
                </StyledAccordionItemHeading>

                <StyledAccordionItemPanel>
                  <UlCol>
                    {getPercentiles(data.results).map((range, index) => (
                      <RangeFilter r={range} key={`rangem-${index}`} />
                    ))}
                  </UlCol>
                </StyledAccordionItemPanel>
              </>
            )}
          </AccordionItemState>
        </StyledAccordionItem>

        {attrKeys
          ?.filter(
            (attrKey) => attrKey.values.length > 1 && attrKey.hideFilter !== 1,
          )
          .map(
            (attrKey) =>
              attrKey.keyLabel &&
              attrKey.values.length > 0 && (
                <StyledAccordionItem key={`attr-key-m-${attrKey.id}`}>
                  <AccordionItemState>
                    {({ expanded }) => (
                      <>
                        <Separator />

                        <StyledAccordionItemHeading expanded={expanded}>
                          <StyledAccordionItemButton
                            $selected={
                              attributes.find((attr) =>
                                attrKey.values
                                  .map((value) => value.id)
                                  .includes(attr.id),
                              ) !== undefined
                            }
                          >
                            <WxLabel label={attrKey.keyLabel} black />

                            <BackIcon
                              icon={expanded ? faChevronUp : faChevronDown}
                            />
                          </StyledAccordionItemButton>
                        </StyledAccordionItemHeading>

                        <StyledAccordionItemPanel>
                          <UlRow>
                            {attrKey.values
                              .sort((a, b) => {
                                if (a.valueSorting === null) return 1;
                                else if (b.valueSorting === null) return -1;
                                else return a.valueSorting - b.valueSorting;
                              })
                              .map((value) => (
                                <AttributeFilter
                                  key={`mobile-attr-${value.code}`}
                                  attribute={value}
                                />
                              ))}
                          </UlRow>
                        </StyledAccordionItemPanel>
                      </>
                    )}
                  </AccordionItemState>
                </StyledAccordionItem>
              ),
          )}
      </Accordion>
    </MobileOnly>
  );
};

const StyledAccordionItemButton = styled(AccordionItemButton)<{
  $selected?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  position: relative;
  height: 1.25rem;
  cursor: pointer;

  ${({ $selected }) =>
    $selected === true &&
    css`
      &::before {
        content: '';
        height: 1.25rem;
        position: absolute;
        left: -1.25rem;
        top: -0.3125rem;
        border-left: 2px solid ${({ theme }) => theme.colors.red};
      }
    `}
`;

const StyledAccordionItem = styled(AccordionItem)`
  position: relative;
  padding-block: 0.8125rem;

  ul {
    display: flex;
    gap: 0.7rem;
    flex-wrap: wrap;
    margin-top: 0.625rem;
  }
`;

const StyledAccordionItemPanel = styled(AccordionItemPanel)`
  padding-bottom: 0.625rem;
`;

const DivOnlyAvailable = styled.div`
  margin-top: 0.625rem;
`;

const Separator = styled.div`
  position: absolute;
  left: -2rem;
  top: 0;
  height: 1px;
  width: 100vw;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const UlCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const UlRow = styled.div`
  display: flex;
  gap: 0.625rem;
`;

const StyledAccordionItemHeading = styled(AccordionItemHeading)<{
  expanded?: boolean;
}>`
  ${({ expanded }) =>
    expanded &&
    css`
      margin-bottom: 0.625rem;
    `}
`;
